import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, Typography, Button, Paper, Container,
  Tabs, Tab, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';

interface Transaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  date: string;
}

const HomePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [uploading, setUploading] = useState(false);
  const [receiptResult, setReceiptResult] = useState<any>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('bt_transactions');
      if (saved) setTransactions(JSON.parse(saved));
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('bt_transactions', JSON.stringify(transactions));
    } catch {}
  }, [transactions]);

  const balance = useMemo(() => transactions.reduce((acc, t) => acc + (t.type === 'income' ? t.amount : -t.amount), 0), [transactions]);

  const addTx = (type: 'income' | 'expense') => {
    if (!description || !amount) return;
    const val = parseFloat(amount);
    if (Number.isNaN(val)) return;
    const tx: Transaction = {
      id: Date.now(),
      description,
      amount: val,
      type,
      date: new Date().toLocaleString()
    };
    setTransactions([tx, ...transactions]);
    setDescription('');
    setAmount('');
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setReceiptResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API_BASE}/api/process-receipt/`, {
        method: 'POST',
        body: form
      });
      const data = await res.json();
      setReceiptResult(data);
    } catch (e) {
      setReceiptResult({ error: 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Budget Tracker
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Budget" />
          <Tab label="Upload Receipt" />
          <Tab label="Expenses" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Balance: ${balance.toFixed(2)}</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" />
                <TextField label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} size="small" />
                <Button variant="contained" color="success" onClick={() => addTx('income')}>Add Income</Button>
                <Button variant="contained" color="error" onClick={() => addTx('expense')}>Add Expense</Button>
              </Box>
            </Paper>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center">No transactions</TableCell></TableRow>
                  ) : (
                    transactions.map(t => (
                      <TableRow key={t.id}>
                        <TableCell>{t.date}</TableCell>
                        <TableCell>{t.description}</TableCell>
                        <TableCell align="right" sx={{ color: t.type==='income'?'success.main':'error.main', fontWeight: 'bold' }}>
                          {t.type==='income' ? '+' : '-'}${t.amount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{t.type}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button variant="contained" component="label" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Select Receipt'}
                <input hidden type="file" accept="image/*" onChange={(e) => e.target.files && handleUpload(e.target.files[0])} />
              </Button>
            </Box>
            {receiptResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Result</Typography>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(receiptResult, null, 2)}</pre>
              </Box>
            )}
          </Paper>
        )}

        {tab === 2 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>All Expenses</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.filter(t=>t.type==='expense').map(t => (
                    <TableRow key={t.id}>
                      <TableCell>{t.date}</TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell align="right">-${t.amount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default HomePage;

